/**
 * Client-side Quest Types and Definitions
 * 
 * Re-exports shared types and includes quest definitions for client use.
 */

import type {
  QuestDefinition,
  MentorNPC,
  StarterQuestId,
  QuestProgress,
  PlayerQuestState,
} from '../../../shared/types/quests';

// Re-export shared types
export type {
  StarterQuestId,
  QuestStatus,
  QuestDefinition,
  QuestReward,
  QuestProgress,
  PlayerQuestState,
  QuestUpdateEvent,
  MentorNPC,
} from '../../../shared/types/quests';

// Captain Aldric - the starter voyage mentor
export const CAPTAIN_ALDRIC: MentorNPC = {
  id: 'captain_aldric',
  name: 'Captain Aldric',
  title: 'Retired Sea Captain',
  portrait: '/assets/portraits/pirates/pirate_7.png',
};

// Starter Voyage Quest Definitions
export const STARTER_VOYAGE_QUESTS: QuestDefinition[] = [
  {
    id: 'bon_voyage',
    order: 0,
    name: 'Bon Voyage',
    description: 'Your maiden voyage begins. Captain Aldric will teach you the fundamentals of seafaring.',
    objective: 'Complete Captain Aldric\'s training: Sail 100 tiles, Dock at an island, Catch 3 fish',
    hint: 'The Grand Latitude is vast and unforgiving. Master these basics before venturing into danger.',
    targetCount: 100,
    objectives: [
      { eventType: 'distance_traveled', targetCount: 100, weight: 0.34 },
      { eventType: 'island_docked', targetCount: 1, weight: 0.33 },
      { eventType: 'fish_caught', targetCount: 3, weight: 0.33 },
    ],
    rewards: [
      { type: 'pieces', amount: 50000 },
      { type: 'xp', amount: 1000 },
    ],
    questGiver: {
      id: 'captain_aldric',
      name: 'Captain Aldric',
      title: 'Retired Sea Captain',
      portrait: '/assets/portraits/pirates/pirate_7.png',
    },
    lore: 'Captain Aldric spent forty years sailing the Grand Latitude before a kraken attack off the Eastern Reach nearly claimed his life. Now he mentors young captains, passing on the knowledge that kept him alive through decades of perilous voyages. The sea is unforgiving, but those who respect her rules can thrive.',
    mentorDialogue: {
      start: "Ahoy there, young captain! I'm Captain Aldric, and I've sailed these waters for forty years. Before you can conquer the Grand Latitude, you must learn to respect her. I'll teach you three essential skills: navigation, docking, and fishing. The sea shows no mercy to the unprepared. Let's begin!",
      progress: "You're making good progress! The sea tests all who sail her. Keep working through these training objectives—they'll save your life one day.",
      complete: "Excellent work! You've proven yourself worthy of the seas. You've mastered the fundamentals, and now the Grand Latitude awaits your exploration. Here's your reward—use it wisely. Fair winds and following seas, captain!",
    },
  },
  {
    id: 'first_steps',
    order: 1,
    name: 'First Steps',
    description: 'Venture beyond the safe harbor and explore the mysteries of the Grand Latitude.',
    objective: 'Visit three different trading posts and gather intelligence about the world.',
    hint: 'The trading posts are hubs of commerce and gossip. Merchants know secrets the Navy would pay for.',
    targetCount: 3,
    objectives: [
      { eventType: 'location_visited', targetCount: 1, weight: 0.34, locationId: 'trading_post_paradise' },
      { eventType: 'location_visited', targetCount: 1, weight: 0.33, locationId: 'trading_post_eastern_reach' },
      { eventType: 'location_visited', targetCount: 1, weight: 0.33, locationId: 'trading_post_arcadia' },
    ],
    rewards: [
      { type: 'pieces', amount: 75000 },
      { type: 'xp', amount: 1500 },
    ],
    questGiver: {
      id: 'captain_aldric',
      name: 'Captain Aldric',
      title: 'Retired Sea Captain',
      portrait: '/assets/portraits/pirates/pirate_7.png',
    },
    lore: 'The Grand Latitude is divided into three major regions, each controlled by different factions. Paradise is the merchant hub, Eastern Reach is where the Navy maintains order, and Arcadia is a wild frontier. Understanding these regions and their politics is essential to survival.',
    mentorDialogue: {
      start: "Now that you've learned the basics, it's time to explore. The Grand Latitude is divided into regions, each with its own character. Visit the major trading posts—Paradise, Eastern Reach, and Arcadia. Listen to the merchants. They'll tell you which waters are safe and which are... contested.",
      progress: "Good! You're getting a feel for the world. Each trading post has its own culture and secrets. The merchants are already talking about you.",
      complete: "You've seen the world now, captain. You understand that the Grand Latitude is more than just water and islands—it's a living, breathing world of factions, rivalries, and opportunity. Here's your reward. Use what you've learned.",
    },
  },
  {
    id: 'cooks_assistant' as StarterQuestId,
    order: 2,
    name: "The Cook's Assistant",
    description: 'The ship\'s cook needs ingredients for tonight\'s feast. Help gather supplies.',
    objective: 'Collect ingredients: catch 25 fish',
    hint: 'Fish at fishing spots (blue markers) in the water. The best catches come from deeper waters, but they\'re worth the risk.',
    targetCount: 25,
    objectives: [
      { eventType: 'fish_caught', targetCount: 25, weight: 1.0 },
    ],
    rewards: [
      { type: 'pieces', amount: 35000 },
      { type: 'xp', amount: 2000 },
    ],
    questGiver: {
      id: 'ships_cook',
      name: 'Ship\'s Cook',
      title: 'Crew Member',
      portrait: '/assets/portraits/crew/cook.png',
    },
    lore: 'The ship\'s cook has been with Captain Aldric for many years. A well-fed crew is a loyal crew, and loyalty keeps you alive out here. The cook knows the value of morale and understands that a captain who takes care of their crew is a captain who survives.',
    mentorDialogue: {
      start: "The cook's been complaining—says he can't make a proper meal without fresh fish. Help him out by catching what he needs. A well-fed crew is a loyal crew, and loyalty keeps you alive out here. The cook's been with me for twenty years; he knows what he's talking about.",
      progress: "Keep catching those fish. The cook's counting on you, and word travels fast on a ship. The crew's already noticing your effort.",
      complete: "Perfect! The cook's got everything he needs now. Here's your reward—and trust me, you'll want to taste what he makes with these. A captain who takes care of their crew is a captain who survives.",
    },
  },
  {
    id: 'message_courier' as StarterQuestId,
    order: 3,
    name: 'The Message Courier',
    description: 'A merchant in Paradise needs messages delivered to contacts in distant ports. A simple task, but one that reveals the hidden networks of the Grand Latitude.',
    objective: 'Deliver messages between three locations: Paradise → Eastern Reach → Arcadia → Paradise',
    hint: 'The merchant guilds control information flow across the Grand Latitude. This job will teach you how the real trade works—not goods, but secrets.',
    targetCount: 3,
    objectives: [
      { eventType: 'npc_talked', targetCount: 1, weight: 0.34, npcId: 'merchant_paradise' },
      { eventType: 'npc_talked', targetCount: 1, weight: 0.33, npcId: 'merchant_eastern_reach' },
      { eventType: 'npc_talked', targetCount: 1, weight: 0.33, npcId: 'merchant_arcadia' },
    ],
    rewards: [
      { type: 'pieces', amount: 60000 },
      { type: 'xp', amount: 2500 },
    ],
    questGiver: {
      id: 'merchant_vex',
      name: 'Merchant Vex',
      title: 'Information Broker',
      portrait: '/assets/portraits/merchants/merchant_vex.png',
    },
    lore: 'Merchant Vex controls the information networks across the Grand Latitude. He started with nothing and learned that gold buys ships, but information buys empires. Every captain, every merchant, every pirate wants to know what Vex knows. The merchant guilds are the true power in the Grand Latitude—they control who gets rich and who starves.',
    mentorDialogue: {
      start: "I've got a contact in Paradise—a merchant named Vex. He needs messages delivered to his associates in Eastern Reach and Arcadia, then back to him. It's simple work, but pay attention to what you hear. The merchant guilds are the true power in the Grand Latitude. The Navy controls the seas, the Pirates raid the trade routes, but the Merchants? They control who gets rich and who starves. This job will teach you how.",
      progress: "You're learning how the real world works, captain. Every message you deliver is a thread in the web that holds the Grand Latitude together. The merchants are watching you.",
      complete: "Excellent work. You've proven yourself trustworthy—that's more valuable than gold out here. Vex will remember this. The merchant guilds don't forget those who serve them well. Here's your reward, and something more valuable: you now have friends in the right places.",
    },
  },
];

// Helper to get quest by ID
export const getQuestById = (id: StarterQuestId): QuestDefinition | undefined => {
  return STARTER_VOYAGE_QUESTS.find(q => q.id === id);
};

// Helper to get quest by order
export const getQuestByOrder = (order: number): QuestDefinition | undefined => {
  return STARTER_VOYAGE_QUESTS.find(q => q.order === order);
};

// Create default quest state for new players
export const createDefaultQuestState = (): PlayerQuestState => {
  const quests: Record<StarterQuestId, QuestProgress> = {} as Record<StarterQuestId, QuestProgress>;
  
  STARTER_VOYAGE_QUESTS.forEach((quest, index) => {
    const progress: QuestProgress = {
      questId: quest.id,
      status: index === 0 ? 'active' : 'locked',
      currentCount: 0,
    };
    
    // Initialize metadata for quests with objectives
    if (quest.objectives && quest.objectives.length > 0) {
      progress.metadata = {};
      quest.objectives.forEach((obj) => {
        progress.metadata![obj.eventType] = 0;
      });
    }
    
    quests[quest.id] = progress;
  });
  
  return {
    starterVoyageComplete: false,
    starterVoyageStartedAt: Date.now(),
    currentQuestIndex: 0,
    quests,
  };
};

